import { Feature, LineString, Point, Position } from "geojson";
import geojsonRbush, { RBush } from "geojson-rbush";

import { point } from "fond/turf";

/** Tolerance in degrees (~0.11m at equator), matching codebase convention */
const ENDPOINT_TOLERANCE = 0.000001;

interface EndpointProperties {
  featureId: string;
}

export interface EndpointIndex {
  tree: RBush<Point, EndpointProperties>;
  featureMap: Map<string, Feature<LineString>>;
}

/**
 * Builds a spatial index of all LineString endpoints from the given features.
 * Non-LineString features, features without an id, and LineStrings with fewer
 * than 2 coordinates are silently skipped.
 */
export function buildEndpointIndex(features: Feature[]): EndpointIndex {
  const tree = geojsonRbush<Point, EndpointProperties>();
  const featureMap = new Map<string, Feature<LineString>>();
  const points: Array<Feature<Point, EndpointProperties>> = [];

  for (const feature of features) {
    const featureId = String(feature.id ?? feature.properties?.id ?? "");
    const coords = (feature.geometry as LineString | undefined)?.coordinates;

    if (feature.geometry?.type === "LineString" && featureId && coords && coords.length >= 2) {
      featureMap.set(featureId, feature as Feature<LineString>);
      points.push(point(coords[0], { featureId }) as Feature<Point, EndpointProperties>);
      points.push(point(coords[coords.length - 1], { featureId }) as Feature<Point, EndpointProperties>);
    }
  }

  tree.load(points);
  return { tree, featureMap };
}

export function isCrossing(feature: Feature<LineString>): boolean {
  const props = feature.properties;
  return props?.Crossing === "YES" || props?.user_Crossing === "YES";
}

/**
 * Starting from the given feature, finds all LineStrings transitively
 * connected via co-located endpoints using BFS.
 *
 * Crossings are included in the result but the BFS does not traverse
 * through them — they act as leaf nodes in the chain.
 *
 * Two endpoints are considered co-located if they fall within
 * ENDPOINT_TOLERANCE degrees of each other in both lng and lat.
 */
export function findConnectedChain(index: EndpointIndex, startFeatureId: string): Set<string> {
  const { tree, featureMap } = index;
  const startFeature = featureMap.get(startFeatureId);
  if (!startFeature) return new Set();

  const visited = new Set<string>();
  const queue: string[] = [startFeatureId];
  visited.add(startFeatureId);

  let queueIndex = 0;
  while (queueIndex < queue.length) {
    const currentId = queue[queueIndex];
    queueIndex += 1;
    const current = featureMap.get(currentId);

    if (current) {
      const coords = current.geometry.coordinates;
      const endpoints: Position[] = [coords[0], coords[coords.length - 1]];

      for (const endpoint of endpoints) {
        const neighborIds = findFeaturesAtEndpoint(tree, endpoint);
        for (const neighborId of neighborIds) {
          if (!visited.has(neighborId)) {
            visited.add(neighborId);
            const neighbor = featureMap.get(neighborId);
            // Crossings are selected but not traversed further
            if (neighbor && !isCrossing(neighbor)) {
              queue.push(neighborId);
            }
          }
        }
      }
    }
  }

  return visited;
}

/**
 * Returns all feature IDs whose endpoints are co-located with the
 * given position (within ENDPOINT_TOLERANCE).
 */
export function findFeaturesAtEndpoint(tree: RBush<Point, EndpointProperties>, position: Position): string[] {
  const [lng, lat] = position;
  const tol = ENDPOINT_TOLERANCE;

  return tree.search([lng - tol, lat - tol, lng + tol, lat + tol]).features.map((f) => f.properties!.featureId);
}
